import instance, { ApiResponse } from 'api';
import { AxiosError, AxiosResponse } from 'axios';
import { BaseApi } from 'baseApi';
import { endpoints } from 'endpoints.config';
import { PackagePromo } from 'subscriptionOptions/types';
import {
  ISchoolDetails,
  ISchoolUser,
  ISchoolUserFreeTrial,
  SubscribeParser,
} from 'pages/subscribe/models';

const UNKOWN_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.';

export class SubscribeApi extends BaseApi {
  public static getPackages = async (quotesId: number | null): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.public.subscription.getSubscriptionsV2 + "?QuotesId=" + quotesId
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  // get subscription packages
  public static getSubscriptionsWithPromocode = async (
    quotesId: number | null,
    promoCode: string
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.public.subscription.getSubscriptionsWithPromocodeV2 +
          `?QuotesId=${quotesId}&promoCode=${promoCode}`
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static validateSchoolInfo = async (
    values: ISchoolDetails
  ): Promise<any> => {
    try {
      const payload = SubscribeParser.buildSchoolDetailsPayload(values);

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.validateSchoolInfo,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static createSchoolUser = async (
    values: ISchoolUser,
    schoolDetails: ISchoolDetails
  ): Promise<any> => {
    try {
      const payload = SubscribeParser.buildSchoolUserDetailsPayload(
        values,
        schoolDetails
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.createsSchoolUser,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static createSchoolUserFreeTrial = async (
    values: ISchoolUserFreeTrial,
    schoolDetails: ISchoolDetails
  ): Promise<any> => {
    try {
      delete (schoolDetails as any).country;
      delete (schoolDetails as any).countryList;
      delete (schoolDetails as any).selectedCountry;

      const payload = SubscribeParser.buildSchoolUserDetailsPayloadFreeTrial(
        values,
        schoolDetails
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.createsSchoolUserFreeTrial,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static subscribeByInvoice = async (
    quotesId: number | null,
    registrationToken: string,
    packagePromos: PackagePromo[],
    expectedGrossAmount: number,
    poNumber: string,
    FinanceEmail: string
  ): Promise<any> => {
    try {
      const payload = SubscribeParser.buildArchiveNowPayload(
        quotesId,
        registrationToken,
        packagePromos,
        expectedGrossAmount,
        poNumber,
        FinanceEmail
      );
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.subscribeByInvoice,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static subscribeByCard = async (
    quotesId: number | null,
    packagePromos: PackagePromo[],
    total: number,
    token: any,
    registrationToken: string,
    poNumber: string,
    FinanceEmail: string
  ): Promise<any> => {
    try {
      const payload = SubscribeParser.buildSubscribeByCardPayload(
        quotesId,
        packagePromos,
        total,
        token,
        registrationToken,
        poNumber,
        FinanceEmail
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.subscribeByCard,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static updateAfter3DSecure = async (values: any): Promise<any> => {
    try {
      const payload = SubscribeParser.buildUpdateAfter3DSecurePayload(values);
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.updateAfter3DSecure,
        payload
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static getFreeTrialQuestionsInfo = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.public.subscription.getFreeTrialQuestionsInfo
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static searchSchoolURN = async (searchUrn: string): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.searchUrn,
        { searchUrn }
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static validateSchoolSize = async (
    urn: string,
    pupilCount: number,
    generalPackagesId: number | null
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.validateSchoolSize,
        { urn, pupilCount, generalPackagesId }
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static validateVatNumber = async (
    freeTrialVatCode: string,
    countryId: number
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.validateVatNumber,
        {
          VatNumber: freeTrialVatCode,
          CountriesId: countryId
        }
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  /**
   * The packages a promo code is valid for. An invalid, expired or uncovering code is refused
   * outright, so an error here is the rejection signal rather than something inferred from price.
   */
  public static checkPromoCode = async (promoCode: string): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<{ packagesIds: number[] }>> = await instance.get(
        endpoints.public.subscription.checkPromoCode +
          '?PromoCode=' +
          encodeURIComponent(promoCode)
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        // details wraps the ids.
        const details = result.data.details;
        return SubscribeApi.successResponse((details && details.packagesIds) || []);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data && error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static saveQuote = async (
    packagesID: number,
    subjectIDs: number[],
    countriesID: number,
    vatNumber: string,
    pupilCount: number,
    promoCodes: string[],
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.public.subscription.saveQoute,
        {
          generalPackagesId: packagesID,
          subjectsIds: subjectIDs,
          countriesId: countriesID,
          vatNumber: vatNumber || null,
          pupilCount: pupilCount,
          promoCode1: promoCodes?.[0] || null,
          promoCode2: promoCodes?.[1] || null,
        }
      );

      if (SubscribeApi.isResponseSuccessful(result.data)) {
        const data = result.data;
        return SubscribeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscribeParser.parseError(errors);
          return SubscribeApi.errorResponse(message);
        }
      }
      return SubscribeApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };  
}
